import React from 'react';
import { Helmet } from 'react-helmet-async';

// Helper to sanitize canonical URLs (removes query params and hash)
function sanitizeCanonical(url) {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}

// Helper to validate schema JSON-LD (must be an object, not array, and serializable)
function isValidSchemaJsonLd(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  try {
    JSON.stringify(obj);
    return true;
  } catch {
    return false;
  }
}

export default function SEOHead({
  title, 
  description, 
  keywords, 
  canonical,
  ogTitle, 
  ogDescription, 
  ogImage, 
  twitterTitle,
  twitterDescription,
  twitterImage,
  noindex,
  nofollow,
  author = 'YTProud',
  themeColor = '#2563eb',
  schemaEnabled,
  ...rest
}) {
  // Fallbacks
  const finalTitle = title || 'YTProud - Social Media Account Marketplace';
  const finalDescription = description || 'Buy and sell verified social media accounts including YouTube, TikTok, Instagram, and Facebook pages.';
  const finalKeywords = keywords || 'youtube channels, tiktok accounts, instagram accounts, facebook pages, social media marketplace';
  const rawCanonical = canonical || (typeof window !== 'undefined' ? window.location.href : undefined);
  const finalCanonical = rawCanonical ? sanitizeCanonical(rawCanonical) : undefined;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;
  const finalOgImage = ogImage || '/images/og-default.jpg';
  const finalTwitterTitle = twitterTitle || finalTitle;
  const finalTwitterDescription = twitterDescription || finalDescription;
  const finalTwitterImage = twitterImage || '/images/twitter-default.jpg';
  const robotsContent = `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;
  const validSchema = isValidSchemaJsonLd(rest.schemaJsonLd);

  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": ogTitle || title,
    "url": typeof window !== 'undefined' ? window.location.origin : '',
    "logo": ogImage,
    "description": ogDescription || description,
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robotsContent} />
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}
      {/* Preload banner image for better performance */}
      <link rel="preload" as="image" href="/images/logo.png" />
      {/* Open Graph */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="YTProud" />
      <meta property="og:url" content={finalCanonical} />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      <meta name="twitter:url" content={finalCanonical} />
      {/* Author & Theme */}
      <meta name="author" content={author} />
      <meta name="theme-color" content={themeColor} />
      {/* Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {/* Schema.org Structured Data */}
      {validSchema && (
        <script type="application/ld+json">
          {JSON.stringify(rest.schemaJsonLd)}
        </script>
      )}
      {schemaEnabled && (
        <script type="application/ld+json">
          {JSON.stringify(schemaJsonLd)}
        </script>
      )}
    </Helmet>
  );
} 